import React from 'react';
import moment from 'moment';

// Services & Helpers
import FormHelper from 'helpers/FormHelper';
import API from 'API';

// Components
import Loader from 'components/common/Loader';
import SuperTable from 'components/common/SuperTable';
import EditNonBookingApptTypeModal from 'components/setup/EditNonBookingApptTypeModal';
import EditRoutingProfileModal from 'components/setup/EditRoutingProfileModal';

//-----------------------------------------------------------------

class SetupBooking extends React.Component {

    constructor(props) {
        super(props);
        
        this.editNonBookingApptTypeModalRef = React.createRef();
        this.editRoutingProfileModalRef = React.createRef();

        this.state = {
            isLoading: true,
            isLoadingNonBookingApptTypes: true
        };

        this.form = new FormHelper({
            fields: {
                currentRoutingProfileID: {
                    label: 'Current Routing Profile',
                    type: 'single-select',
                    getOptions: () => this.state.routingProfiles.map(rp => ({
                        value: rp.id,
                        text: rp.name
                    }))
                },
                bookingTimeSlotType: {
                    label: 'Time Slot Type',
                    type: 'single-select',
                    getOptions: () => [
                        { value: 'TimeRange', text: 'Arrival Time Range (-30min to +30min)' },
                        { value: 'SpecificTime', text: 'Specific Arrival Time' },
                        { value: 'AMorPM', text: 'AM or PM Arrival Time' }
                    ]
                },
                cloneApptIfCancelledDiffDate: {
                    type: 'checkbox',
                    boxLabel: 'Clone cancelled appt if date doesn\'t match'
                },
                bookingAgentAccountIDs: {
                    type: 'checkboxes',
                    label: 'Booking agent(s)',
                    wrapperClassName: 'checkboxes',
                    getOptions: () => this.state.bookingAgents.map(ba => ({ value: ba.id, text: ba.name }))
                },
                stripePublicKey: {
                    type: 'text',
                    label: 'Stripe Publishable Key'
                },
                stripeSecretKey: {
                    type: 'password',
                    label: 'Stripe Secret Key'
                }
            },
            getValue: (fieldName) => this.state.account[fieldName],
            setValue: (fieldName, value) => this.updateFields({ [fieldName]: value })
        });
    }

    componentDidMount() {
        this.props.fns.setTitle('Booking setup');
        this.load();
    }

    async load() {
        this.setState({ isLoading: true });
        const account = await API.call('account/get-current');
        const bookingAgents = await API.call('account/list-booking-agents');
        const routingProfiles = await API.call('auto-planner/list-profiles');
        this.setState({
            isLoading: false,
            account,
            bookingAgents,
            routingProfiles
        }, () => {
            this.loadNonBookingApptTypes();
        });
    }

    async save() {
        const { account } = this.state;
        this.setState({ isLoading: true });
        await API.call('account/save-current/booking', account);
        this.props.fns.checkLogin();
        this.props.history.replace('/setup');
    }   

    updateFields(fields, callback) {
        const account = { ...this.state.account };
        for (let fieldName in fields) {
            const value = fields[fieldName];
            account[fieldName] = value;
        }
        this.setState({ account }, callback);
    }

    async loadNonBookingApptTypes() {
        this.setState({ isLoadingNonBookingApptTypes: true });
        const nonBookingApptTypes = await API.call('non-booking-appt-type/list');
        this.setState({
            nonBookingApptTypes,
            isLoadingNonBookingApptTypes: false
        });
    }

    async addNonBookingApptType() {
        await this.editNonBookingApptTypeModalRef.current.open({
        });
        this.load();
    }
    
    async editNonBookingApptType(id) {
        await this.editNonBookingApptTypeModalRef.current.open({
            id
        });
        this.load();
    }

    updateCalendarApptInfo(index, value) {
        const calendarApptInfos = [ ...this.state.account.calendarApptInfos || [] ];
        if (index == -1) {
            calendarApptInfos.push(value);
        } else if (value) {
            calendarApptInfos[index] = value;
        } else {
            calendarApptInfos.splice(index, 1);
        }
        this.updateFields({ calendarApptInfos });
    }

    async addRoutingProfile() {
        await this.editRoutingProfileModalRef.current.open({
        });
        this.load();
    }

    async editRoutingProfile(id) {
        await this.editRoutingProfileModalRef.current.open({
            id
        });
        this.load();
    }

    //---------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isLoading
        } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>

            <form onSubmit={e => { e.preventDefault(); this.save() }}>

                <section className="control-panel sticky">

                    <h1>Booking</h1>

                    <button type="submit" className="ms-auto btn btn-primary">
                        Save
                    </button>

                </section>

                <div className="row">

                    <div className="col-md-6">

                        {this.renderRoutePlanningSection()}

                        {this.renderNonBookingApptTypesSection()}

                    </div>

                    <div className="col-md-6">

                        {this.renderBookingOptionsSection()}

                    </div>

                </div>

            </form>

            <EditRoutingProfileModal ref={this.editRoutingProfileModalRef} />
        </>);
    }

    renderRoutePlanningSection() {
        const { routingProfiles, account } = this.state;

        return (
            <section>
                
                <h2>
                    Routing

                    <button type="button" className="ms-auto btn btn-primary" onClick={() => this.addRoutingProfile()}>
                        Add Profile
                    </button>

                </h2>

                {this.form.render([
                    'currentRoutingProfileID'
                ])}

                <SuperTable
                    className="price-schemes-table table table-bordered clickable mb-0"
                    rows={routingProfiles}
                    keyAccessor={routingProfile => routingProfile.id}
                    cols={{
                        name: { label: 'Profiles' },
                        autoSwitchMonth: {
                            label: 'Auto Switch',
                            getValue: (colInfo, routingProfile) => {
                                if (routingProfile.autoSwitchMonth) {
                                    return moment(`2000-${routingProfile.autoSwitchMonth}-01`).format('MMM');
                                }
                            }
                        }
                    }}
                    onClick={(routingProfile, e) => this.editRoutingProfile(routingProfile.id)}
                    emptyText="No routing profiles set up"
                />
                    
            </section>
        );
    }

    renderBookingOptionsSection() {
        const { account } = this.state;

        return (
            <section>

                <fieldset>

                    <h2>Booking Options</h2>

                    {this.form.render([
                        'bookingTimeSlotType',
                        'cloneApptIfCancelledDiffDate',
                        'bookingAgentAccountIDs'
                    ])}

                    {this.renderCalendarApptInfos()}

                </fieldset>

            </section>
        );
    }

    renderNonBookingApptTypesSection() {
        const { isLoadingNonBookingApptTypes, nonBookingApptTypes } = this.state;

        if (isLoadingNonBookingApptTypes) {
            return (
                <section className="control-panel sticky">
                    <Loader />
                </section>
            );
        }
        
        return (<>

            <section>
                
                <h2>
                    Non-Booking Appointment Types

                    <button type="button" className="ms-auto btn btn-primary" onClick={() => this.addNonBookingApptType()}>
                        Add Type
                    </button>

                </h2>
                
                <SuperTable
                    className="non-booking-appt-types-table table table-bordered clickable mb-0"
                    rows={nonBookingApptTypes}
                    keyAccessor={nbat => nbat.id}
                    cellPropsAccessor={(colInfo, nbat) => {
                        if (nbat && colInfo.name == 'colour' && nbat.backColour) {
                            return {
                                style: {
                                    backgroundColor: nbat.backColour,
                                    color: nbat.textColour
                                }
                            };
                        }
                    }}
                    cols={{
                        colour: {
                            className: 'colour-col',
                            getValue: (colInfo, row, formattedValue, index) => `${index + 1}`
                        },
                        name: { label: 'Name', className: 'name-col' },
                        location: { label: 'Location', className: 'location-col' }
                    }}
                    onClick={(nbat, e) => this.editNonBookingApptType(nbat.id)}
                    emptyText="No non-booking appointment types to show"
                />

            </section>

            <EditNonBookingApptTypeModal
                ref={this.editNonBookingApptTypeModalRef}
            />

        </>);
    }

    renderCalendarApptInfos() {
        const calendarApptInfos = [...this.state.account.calendarApptInfos || []];
        calendarApptInfos.push(null);

        const options = [
            { value: 'CustomerFullName', text: 'Customer - Full Name' },
            { value: 'CustomerFullNameOrCompany', text: 'Customer - Full Name or Company Name' },
            { value: 'CustomerLastName', text: 'Customer - Last Name' },
            { value: 'CustomerLastNameOrCompany', text: 'Customer - Last Name or Company Name' },
            { value: 'CustomerIsNew', text: 'Customer - New Indicator' },
            { value: 'CustomerCompany', text: 'Customer - Company Name' },
            { value: 'PropertyAddress', text: 'Property - Address' },
            { value: 'PropertyPostcode', text: 'Property - Postcode' },
            { value: 'ApptSummary', text: 'Job Summary' },
            { value: 'ApptTime', text: 'Time' },
            { value: 'TechnicianNickname', text: 'Technician Name' }
        ];
        return (
            <div className="form-group">
                <label className="form-label">Calendar appointment information</label>
                <table className="table table-bordered mb-0">
                    <tbody>
                        {calendarApptInfos.map((cai, index) =>
                            <tr key={index}>
                                <td>
                                    <select
                                        className="form-control"
                                        value={cai || ''}
                                        onChange={e => this.updateCalendarApptInfo(index, e.target.value)}
                                    >
                                        <option value="">{index == calendarApptInfos.length - 1 ? '(Add...)' : '(Remove)'}</option>
                                        {options.map(o =>
                                            <option key={o.value} value={o.value}>{o.text}</option>
                                        )}
                                    </select>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

}

export default SetupBooking;